import { memo, useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import type { PluggableList } from 'unified';
import { useContentScanners } from '../../client/content-scanners';
import { remarkContentScanners } from './markdown/remark-content-scanners';
import { remarkCollectLinkTargets, sameLinkTargets } from './markdown/remark-trailing-links';
import { renderTrailingContentScanners } from './markdown/content-scanner-dispatch';
import { useInView } from './markdown/use-in-view';
import { buildComponents } from './markdown/component-maps';
import { passthroughReflexScanUrlTransform } from './markdown/url-transform';
import { INLINE_HTML_REHYPE_PLUGINS, containsInlineHtml } from './markdown/inline-html';
import { MarkdownAgentIdProvider } from './markdown/MarkdownAgentContext';
import { rehypeSourceLines } from './markdown/source-lines';
import type { MarkdownSize } from './markdown/sizes';

export { CodeBlockShell, ProseCodeBlock, ProseInlineCode } from './markdown/code-blocks';
export { MarkdownAgentIdProvider, useMarkdownAgentId } from './markdown/MarkdownAgentContext';
export {
  SOURCE_LINE_ATTR,
  SOURCE_END_LINE_ATTR,
  SOURCE_LINE_SELECTOR,
} from './markdown/source-lines';

export type { MarkdownSize } from './markdown/sizes';

/** Stable empty list so the no-links case never re-renders on identity. */
const NO_LINKS: readonly string[] = [];

const PROSE_CONTAINER_CLASSES = [
  '[&>h1]:mb-3',
  '[&>h1+p]:!mt-2 [&>h1+p]:!mb-10 [&>h1+p]:text-lg [&>h1+p]:leading-[1.65] [&>h1+p]:text-muted-foreground',
  '[&_h2+p]:!mt-3 [&_h3+p]:!mt-2 [&_h4+p]:!mt-2',
  '[&_p+ul]:!mt-2 [&_p+ol]:!mt-2',
].join(' ');

interface MarkdownContentProps {
  content: string;
  className?: string;
  size?: MarkdownSize;
  /** Optional component overrides shallow-merged on top of the size defaults. */
  components?: Partial<Components>;
  /** Extra remark plugins appended after the built-in scanners. */
  remarkPlugins?: PluggableList;
  /**
   * Render every newline in the source as a hard line break. Enable this for
   * user-authored text (chat messages), where people type literal newlines and
   * expect them preserved. Leave it off for agent/model output and prose docs,
   * where single newlines are CommonMark soft breaks and collapsing them into
   * spaces is the intended behavior.
   */
  preserveLineBreaks?: boolean;
  /** Rehype plugins forwarded to react-markdown, after the built-in ones. */
  rehypePlugins?: PluggableList;
  /**
   * Stamp each rendered block with the source lines it came from
   * (`data-source-line` / `data-source-end-line`). Enable it on surfaces that
   * have to map a spot in the rendered prose back to a line in the file — the
   * filesystem preview anchors a comment that way. Off elsewhere: it is dead
   * weight in the DOM for content that has no file behind it.
   */
  trackSourceLines?: boolean;
  /**
   * Agent id this content belongs to, when known. Exposed to content-scanner
   * components (e.g. the filesystem file-path popover) via context so they can
   * fetch agent-scoped data. Omit it on non-agent surfaces (docs, changelog).
   */
  agentId?: string;
  /**
   * Render `trailing` content scanners — today, link preview cards — after the
   * prose, one per unique linked URL. Opt-in, because "after the prose" only
   * means "under this" on a surface that renders one message: on a document
   * (a docs page, an assessment, a previewed `.md` file) the same rule piles
   * every link in the page at the bottom, far from the sentence that linked
   * it. Set it on message-shaped surfaces only.
   */
  trailingScanners?: boolean;
}

export const MarkdownContent = memo(function MarkdownContent({
  content,
  className = '',
  size = 'sm',
  components,
  remarkPlugins,
  preserveLineBreaks = false,
  rehypePlugins,
  trackSourceLines = false,
  agentId,
  trailingScanners = false,
}: MarkdownContentProps) {
  // Subscribe to the scanner registry so this re-renders (and react-markdown
  // re-parses) the moment plugin scanners register after manifests resolve.
  useContentScanners();
  // Dev guard: an unstable `components` reference defeats the useMemo below
  // and remounts every block on every streaming token.
  const prevComponentsRef = useRef(components);
  useEffect(() => {
    if (
      process.env.NODE_ENV !== 'production' &&
      components !== undefined &&
      prevComponentsRef.current !== components
    ) {
      console.warn(
        '[MarkdownContent] The `components` prop changed reference between renders. ' +
          'Pass a memoized object to avoid remounting every block on each render.',
      );
    }
    prevComponentsRef.current = components;
  });

  // `buildComponents` is already identity-stable; this memo extends that to the
  // merged map so a caller-supplied `components` override does not reintroduce
  // the per-render remount. See `markdown/component-maps.ts` for why identity
  // matters. Callers passing `components` must memoize it themselves.
  const merged: Components = useMemo(
    () => (components ? { ...buildComponents(size), ...components } : buildComponents(size)),
    [size, components],
  );
  // Trailing content scanners (link previews) render after the prose, once per
  // unique linked URL. The collector plugin runs last so it sees the links
  // every earlier plugin produced — `remark-gfm`'s autolinks included — and
  // fills an array this component owns; the effect below promotes it to state.
  // Nothing here runs on a surface that did not opt in: no plugin in the
  // pipeline, no collected links, no observer.
  // See `markdown/remark-trailing-links.ts`.
  const collectedLinks = useRef<string[]>([]);
  const collectLinkTargets = useMemo(() => remarkCollectLinkTargets(collectedLinks.current), []);
  const [trailingLinks, setTrailingLinks] = useState<readonly string[]>(NO_LINKS);
  const remark: PluggableList = [
    remarkGfm,
    ...(preserveLineBreaks ? [remarkBreaks] : []),
    remarkContentScanners,
    ...(remarkPlugins ?? []),
    ...(trailingScanners ? [collectLinkTargets] : []),
  ];
  // Runs after every commit: the parse happens while `<Markdown>` renders, so
  // this is the first point the collected links can be read. `setState` bails
  // out when they are unchanged, which is every render of a block whose links
  // did not move — including every token of a stream once they have.
  useEffect(() => {
    if (!trailingScanners) return;
    const collected = collectedLinks.current;
    setTrailingLinks((prev) => (sameLinkTargets(prev, collected) ? prev : collected.slice()));
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const inView = useInView(containerRef, trailingLinks.length > 0);
  const trailing = renderTrailingContentScanners(trailingLinks, inView);
  // Inline HTML (`<span style="color:#2563eb">`, `<sub>`, `<kbd>`) renders the
  // way it does on GitHub instead of leaking as literal source text. The raw
  // pipeline only engages when the content actually has a tag in it — see
  // `markdown/inline-html.ts` for the sanitize rules that make it safe.
  // Source-line stamping runs last, after the sanitizer: its attributes are not
  // in the sanitize allowlist, and the sanitizer carries positions through, so
  // stamping afterwards is both safe and complete.
  const rehype: PluggableList = [
    ...(containsInlineHtml(content) ? INLINE_HTML_REHYPE_PLUGINS : []),
    ...(rehypePlugins ?? []),
    ...(trackSourceLines ? [rehypeSourceLines] : []),
  ];
  const proseClasses = size === 'prose' ? PROSE_CONTAINER_CLASSES : '';
  const containerClass = [proseClasses, className].filter(Boolean).join(' ');
  const rendered = (
    <div className={containerClass} ref={containerRef}>
      <Markdown
        remarkPlugins={remark}
        rehypePlugins={rehype}
        components={merged}
        urlTransform={passthroughReflexScanUrlTransform}
      >
        {content}
      </Markdown>
      {trailing}
    </div>
  );
  // Only establish the agent-id context when we actually have one, so nested
  // MarkdownContent without an id inherits any ancestor-provided value.
  return agentId ? (
    <MarkdownAgentIdProvider agentId={agentId}>{rendered}</MarkdownAgentIdProvider>
  ) : (
    rendered
  );
});
